import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../../../../ui/toast';

import { ISendInviteRequest } from '../../../../../../../../interfaces';
import { apiUrl } from '../../../../../../../../utils/api_service/api_url';
import { getCandidateApplicants, getInterviewRounds } from '../../../../../../../../utils/api_service/JobOpenings';
import {
  getInterviewApplicants,
  getInterviewRound,
  getInterviewRoundAllApplicants,
  interviewRoundResults,
  sendInvitations,
} from '../../../../../../../../utils/api_service/SmartInterview';
import { createRequest } from '../../../../../../../../utils/helpers/api_utils';
import {
  AllApplicantRequest,
  ApplicantRequest,
} from '../../../../../../../../utils/types/InterviewTypes';

import { Applicant } from '../InviteCandidates';

interface IUseInviteCandidates {
  interviewRoundID: string;
  isOpen: boolean;
  candidateTypes: Record<string, 'Virtual' | 'In-Person'>;
}

export const useInviteCandidates = ({
  interviewRoundID,
  isOpen,
  candidateTypes,
}: IUseInviteCandidates) => {
  const { id } = useParams<{ id: string }>();

  const [searchObject, setSearchObject] = useState<ApplicantRequest>({
    email: '',
    name: '',
    interviewStatus: [],
  });

  const [pageNumber, setPageNumber] = useState(1);
  const [limit, setLimit] = useState(10);

  const [allList, setAllList] = useState<Applicant[]>([]);
  const [initialInvited, setInitialInvited] = useState<Applicant[]>([]);
  const [newSelections, setNewSelections] = useState<Applicant[]>([]);
  const [interviewApplicants, setInterviewApplicants] = useState<Applicant[]>(
    [],
  );
  const [totalRecords, setTotalRecords] = useState(0);

  const totalPages = Math.max(1, Math.ceil(totalRecords / limit));

  const fetchAllAndInvited = useCallback(async () => {
    if (!interviewRoundID || !id) return;

    try {
      // Get all rounds to determine current round index
      const roundsRes = await getInterviewRounds(id);
      const rounds = await roundsRes.json();
      const currentRoundIndex = rounds.findIndex((r: any) => r.id === interviewRoundID);

      // First get all available candidates and invited candidates
      // Invited list must be fetched unfiltered (or the Shortlisted filter hides newly invited Virtual candidates)
      const [allRes, invitedRes] = await Promise.all([
        getInterviewRoundAllApplicants(
          {
            email: searchObject.email || '',
            name: searchObject.name || '',
            applicantStatus: searchObject.interviewStatus,
          },
          pageNumber,
          limit,
          interviewRoundID,
        ),
        getInterviewApplicants(
          { email: '', name: '', interviewStatus: [] },
          1,
          100,
          interviewRoundID,
        ),
      ]);

      const allJson = await allRes.json();
      const invitedJson = await invitedRes.json();
      const totalCount =
        allJson.total_count ?? allJson.totalCount ?? allJson.total ??
        (allJson.applicants || []).length;
      setTotalRecords(totalCount);

      if (currentRoundIndex > 0) {
        // For rounds after first, get previous round results
        const prevRoundRes = await interviewRoundResults(rounds[currentRoundIndex - 1].id);
        const prevRoundData = await prevRoundRes.json();

        // Get emails of candidates who completed previous round with good scores
        const qualifiedEmails = new Set(
          prevRoundData.results
            .filter((result: any) =>
              result.status === 'Completed' &&
              (result.averageScore >= 70 || result.aiOverallScore?.score >= 70),
            )
            .map((result: any) => result.user?.email)
            .filter(Boolean),
        );

        // Filter candidates who completed previous round
        const filteredApplicants = allJson.applicants?.filter(
          (applicant: any) => qualifiedEmails.has(applicant.email),
        ) || [];

        setAllList(filteredApplicants);
      } else {
        // For first round, use all applicants
        setAllList(allJson.applicants || []);
      }

      setInitialInvited(invitedJson.applicants || []);
    } catch (err) {
      toast.error('Error fetching applicants', {
        position: 'bottom-center',
      });
      setAllList([]);
      setInitialInvited([]);
      setTotalRecords(0);
    }
  }, [id, interviewRoundID, limit, pageNumber, searchObject]);
  useEffect(() => {
    if (isOpen) fetchAllAndInvited();
  }, [isOpen, fetchAllAndInvited]);

  // Reset to page 1 whenever the search/filter criteria change so the
  // user doesn't get stuck on a now out-of-range page.
  useEffect(() => {
    setPageNumber(1);
  }, [searchObject]);

  useEffect(() => {
    const blockedIds = new Set([
      ...initialInvited.map((a) => a.id),
      ...newSelections.map((a) => a.id),
    ]);
    const blockedEmails = new Set([
      ...initialInvited.map((a) => a.email),
      ...newSelections.map((a) => a.email),
    ]);

    setInterviewApplicants(
      allList.filter(
        (c) => !blockedIds.has(c.id) && !blockedEmails.has(c.email),
      ),
    );
  }, [allList, initialInvited, newSelections]);

  const handleSelect = (cand: Applicant) =>
    setNewSelections((prev) => [...prev, { ...cand, interviewType: candidateTypes[cand.id] || 'Virtual' }]);
  const handleRemove = (cand: Applicant) =>
    setNewSelections((prev) => prev.filter((a) => a.id !== cand.id));
  const handleSelectAll = () =>
    setNewSelections((prev) => [
      ...prev,
      ...interviewApplicants.map(app => ({
        ...app,
        interviewType: candidateTypes[app.id] || 'Virtual',
      })),
    ]);
  const handleRemoveAll = () => setNewSelections([]);

  const sendAllInvites = async () => {
    if (newSelections.length === 0) {
      toast.info('No new candidates to invite', {
        position: 'bottom-center',
      });
      return;
    }
    try {
      const body: ISendInviteRequest = {
        applicants: newSelections.map((a) => ({
          applicantId: a.id,
          interviewType: candidateTypes[a.id] || a.interviewType || 'Virtual',
        })),
      };
      await sendInvitations(body, interviewRoundID);
      toast.success('Invitations sent!', {
        position: 'bottom-center',
      });

      await fetchAllAndInvited();
      setNewSelections([]);
    } catch (err) {
      toast.error('Error sending invitations', {
        position: 'bottom-center',
      });
    }
  };

  return {
    interviewApplicants,
    selectedCandidates: [...initialInvited, ...newSelections],
    newSelections,
    sendAllInvites,
    totalPages,
    totalRecords,
    pageNumber,
    setPageNumber,
    setLimit,
    setSearchObject,
    handleSelect,
    handleRemove,
    handleSelectAll,
    handleRemoveAll,
    allList,
  };
};
